import React from 'react';
import {
    Autocomplete,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
} from '@mui/material';
import { MetadataFieldDef } from '../AssetExplorer.types';

export interface MetadataFieldInputProps {
    field: MetadataFieldDef;
    value: unknown;
    onChange: (fieldKey: string, value: unknown) => void;
    disabled?: boolean;
}

const asString = (value: unknown): string => (value == null ? '' : String(value));

const asStringArray = (value: unknown): string[] => {
    if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
    if (value == null || value === '') return [];
    return [String(value)];
};

export const MetadataFieldInput: React.FC<MetadataFieldInputProps> = ({
    field,
    value,
    onChange,
    disabled = false,
}) => {
    const helper = field.helpText || undefined;
    const label = field.required ? `${field.label} *` : field.label;

    if (field.type === 'TEXTAREA') {
        return (
            <TextField
                label={label}
                value={asString(value)}
                onChange={(e) => onChange(field.fieldKey, e.target.value)}
                fullWidth
                multiline
                minRows={3}
                disabled={disabled}
                color="secondary"
                helperText={helper}
            />
        );
    }

    if (field.type === 'CHECKBOX') {
        const selected = new Set(asStringArray(value));
        return (
            <FormControl component="fieldset" variant="standard" disabled={disabled} fullWidth>
                <FormLabel color="secondary">{label}</FormLabel>
                <FormGroup>
                    {field.options.map((opt) => (
                        <FormControlLabel
                            key={opt.value}
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={selected.has(opt.value)}
                                    onChange={(e) => {
                                        const next = new Set(selected);
                                        if (e.target.checked) next.add(opt.value);
                                        else next.delete(opt.value);
                                        onChange(field.fieldKey, [...next]);
                                    }}
                                />
                            }
                            label={opt.label}
                        />
                    ))}
                </FormGroup>
                {helper && <FormHelperText>{helper}</FormHelperText>}
            </FormControl>
        );
    }

    if (field.type === 'DROPDOWN') {
        if (field.multiple) {
            const selected = asStringArray(value);
            return (
                <FormControl fullWidth disabled={disabled}>
                    <InputLabel color="secondary">{label}</InputLabel>
                    <Select
                        multiple
                        label={label}
                        color="secondary"
                        value={selected}
                        onChange={(e) => {
                            const next = e.target.value;
                            onChange(field.fieldKey, typeof next === 'string' ? next.split(',') : next);
                        }}
                        input={<OutlinedInput label={label} />}
                        renderValue={(picked) => (picked as string[]).join(', ')}
                    >
                        {field.options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                <Checkbox color="secondary" checked={selected.includes(opt.value)} />
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {helper && <FormHelperText>{helper}</FormHelperText>}
                </FormControl>
            );
        }

        return (
            <TextField
                select
                label={label}
                value={asString(value)}
                onChange={(e) => onChange(field.fieldKey, e.target.value)}
                fullWidth
                disabled={disabled}
                color="secondary"
                helperText={helper}
            >
                <MenuItem value="">
                    <em>None</em>
                </MenuItem>
                {field.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    if (field.type === 'TAGS') {
        return (
            <Autocomplete
                multiple
                freeSolo
                disabled={disabled}
                options={field.options.map((o) => o.label)}
                value={asStringArray(value)}
                onChange={(_e, next) => onChange(field.fieldKey, next.map((v) => String(v).trim()).filter(Boolean))}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                            <Chip
                                key={key}
                                variant="outlined"
                                color="secondary"
                                label={option}
                                {...tagProps}
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField {...params} label={label} color="secondary" helperText={helper} />
                )}
            />
        );
    }

    return (
        <TextField
            label={label}
            value={asString(value)}
            onChange={(e) => onChange(field.fieldKey, e.target.value)}
            fullWidth
            disabled={disabled}
            color="secondary"
            helperText={helper}
        />
    );
};

export default MetadataFieldInput;
